import { queryOptions } from '@tanstack/react-query'
import type { Database } from '@/types/supabase'
import { supabase } from '@/lib/supabase/client'
import type { Generation } from './generations'

// =============================================================================
// Product Version Types
// =============================================================================

type ProductVersionRow = Database['public']['Tables']['product_versions']['Row']

/**
 * Product version with embedded generation and author profile.
 * Uses proper FK joins through product_versions table.
 */
export interface ProductVersion extends ProductVersionRow {
  generation: Generation
}

export const productVersion = {
  /**
   * Find all versions for a product (version history)
   * Returns versions ordered by version_number descending (newest first)
   * Uses proper FK join through product_versions table
   */
  findManyByProduct: (productId: string) =>
    queryOptions({
      queryKey: ['product-versions', productId],
      queryFn: async (): Promise<ProductVersion[]> => {
        if (!productId || productId.trim() === '') {
          throw new Error(
            'Invalid productId: cannot fetch versions without valid product ID'
          )
        }

        const { data, error } = await supabase
          .from('product_versions')
          .select(
            `
            *,
            generation:generations!generation_id(*, profiles!author(id, email, full_name, avatar_url))
          `
          )
          .eq('product_id', productId)
          .order('version_number', { ascending: false })

        if (error) {
          throw new Error(
            `Failed to fetch product versions: ${error.message}`,
            { cause: error }
          )
        }

        return (data || []) as unknown as ProductVersion[]
      },
    }),

  /**
   * Get the next version number for a product
   */
  getNextVersionNumber: async (productId: string): Promise<number> => {
    if (!productId || productId.trim() === '') {
      throw new Error(
        'Invalid productId: cannot get version number without valid product ID'
      )
    }

    const { data, error } = await supabase
      .from('product_versions')
      .select('version_number')
      .eq('product_id', productId)
      .order('version_number', { ascending: false })
      .limit(1)
      .maybeSingle()

    if (error) {
      throw new Error(`Failed to get next version number: ${error.message}`, {
        cause: error,
      })
    }

    return (data?.version_number ?? 0) + 1
  },

  /**
   * Create a new product version (links product to generation)
   */
  create: async (input: {
    productId: string
    generationId: string
    versionNumber: number
  }): Promise<string> => {
    const { data, error } = await supabase
      .from('product_versions')
      .insert({
        product_id: input.productId,
        generation_id: input.generationId,
        version_number: input.versionNumber,
      })
      .select('id')
      .single()

    if (error) {
      throw new Error(`Failed to create product version: ${error.message}`, {
        cause: error,
      })
    }

    return data.id
  },

  /**
   * Delete a product version by generation ID
   * This removes the version link, not the generation itself (kept for audit)
   */
  deleteByGenerationId: async (productId: string, generationId: string) => {
    const { error } = await supabase
      .from('product_versions')
      .delete()
      .eq('product_id', productId)
      .eq('generation_id', generationId)

    if (error) {
      throw new Error(`Failed to delete product version: ${error.message}`, {
        cause: error,
      })
    }
  },
}
